import { useState, useRef, useEffect } from "react";
import { Message } from "shared/types/chatBoard";
import { getRoleTheme, formatDateTime24h } from "shared/utils/helper";
import { Icons } from "assets";
import CustomBadge from "shared/components/common/customBadge";
import classNames from "classnames";
import styles from "./style.module.scss";

interface MessageItemProps {
  message: Message;
  onReplyClick?: (message: Message) => void;
  variant?: "default" | "replyPanel";
  showReplyButton?: boolean;
  replies?: any[];
  className?: string;
  getFileIcon?: (type: string) => React.ReactNode;
  removeBackgroundAndPadding?: boolean;
}

const MessageItem = ({
  message: msg,
  onReplyClick,
  variant = "default",
  showReplyButton = true,
  replies,
  className,
  getFileIcon,
  removeBackgroundAndPadding = false,
}: MessageItemProps) => {
  const roleTheme = getRoleTheme(msg.sender.role);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuCoordinates, setMenuCoordinates] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const defaultGetFileIcon = (type: string) => {
    if (type === "image") {
      return <Icons.TabImage className={styles.fileIcon} />;
    }
    return <Icons.TabOverview className={styles.fileIcon} />;
  };

  const fileIconGetter = getFileIcon || defaultGetFileIcon;

  const handleReplyClick = () => {
    if (onReplyClick) {
      onReplyClick(msg);
    }
    setDropdownOpen(false);
  };

  // Listen for events to close this dropdown when another opens
  useEffect(() => {
    const handleCloseDropdown = (e: CustomEvent) => {
      const messageId = e.detail?.messageId;
      // Close if this is not the message that should be open
      if (messageId !== msg.id) {
        setDropdownOpen(false);
      }
    };

    window.addEventListener(
      "messageItem:closeDropdown" as any,
      handleCloseDropdown as EventListener,
    );
    return () => {
      window.removeEventListener(
        "messageItem:closeDropdown" as any,
        handleCloseDropdown as EventListener,
      );
    };
  }, [msg.id]);

  // Dispatch event when this dropdown opens to close others
  useEffect(() => {
    if (dropdownOpen) {
      const event = new CustomEvent("messageItem:closeDropdown", {
        detail: { messageId: msg.id },
      });
      window.dispatchEvent(event);
    }
  }, [dropdownOpen, msg.id]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [dropdownOpen]);

  // Calculate menu position
  useEffect(() => {
    if (!dropdownOpen) {
      setMenuCoordinates(null);
      return;
    }

    const updateMenuPosition = () => {
      // Use trigger button for more accurate positioning
      const element = triggerRef.current || dropdownRef.current;
      if (element) {
        const rect = element.getBoundingClientRect();
        // Calculate position relative to viewport
        // For right alignment, we want the menu's right edge to align with trigger's right edge
        // rect.right is the distance from left edge of viewport to right edge of trigger
        // window.innerWidth - rect.right gives us the distance from right edge of viewport
        const rightPosition = window.innerWidth - rect.right;
        const topPosition = rect.bottom + 4;

        setMenuCoordinates({
          top: topPosition,
          right: rightPosition,
        });
      }
    };

    // Use multiple requestAnimationFrame calls to ensure DOM is fully updated
    let rafId1: number;
    let rafId2: number;

    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        updateMenuPosition();
        // One more frame to ensure menu is rendered
        requestAnimationFrame(() => {
          updateMenuPosition();
        });
      });
    });

    // Also update immediately and after a small delay
    updateMenuPosition();
    const timeoutId = setTimeout(() => {
      updateMenuPosition();
    }, 10);

    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);

    return () => {
      clearTimeout(timeoutId);
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [dropdownOpen]);

  const isReplyPanel = variant === "replyPanel";
  const hasReplies = (msg.repliesCount ?? 0) > 0;

  let lastReplyTime = msg.lastReplyAt;
  if (!lastReplyTime && replies && replies.length > 0) {
    const sortedReplies = [...replies].sort((a: any, b: any) => {
      const dateA = new Date(a.created_at || a.createdAt || 0).getTime();
      const dateB = new Date(b.created_at || b.createdAt || 0).getTime();
      return dateB - dateA;
    });
    const mostRecent = sortedReplies[0];
    lastReplyTime = mostRecent?.created_at || mostRecent?.createdAt;
  }

  return (
    <div
      className={classNames(
        isReplyPanel ? styles.selectedMessageItem : styles.messageItem,
        removeBackgroundAndPadding && styles.messageItemCompact,
        "flex items-start gap-2",
        className,
      )}
    >
      <div
        className={classNames(
          styles.avatar,
          !isReplyPanel && "sm:absolute sm:mt-2 mt-0.5",
          styles[msg.sender.theme],
        )}
      >
        {msg.sender.profile_asset ? (
          <img
            src={msg.sender.profile_asset}
            alt={msg.sender.name}
            className={styles.avatarImage}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
              const parent = target.parentElement;
              if (parent && !parent.textContent) {
                parent.textContent = msg.sender.initial;
              }
            }}
          />
        ) : (
          msg.sender.initial
        )}
      </div>

      <div
        className={classNames(
          !isReplyPanel && "sm:pl-12",
          "flex-1",
          isReplyPanel && "relative",
        )}
      >
        <div className={classNames(styles.messageHeader)}>
          <div className={classNames(styles.messageInfo, "")}>
            <div
              className={classNames(
                styles.senderInfo,
                "flex xs:flex-row flex-col xs:items-center flex-wrap xs:gap-2",
              )}
            >
              <div className="flex items-center gap-2">
                <span className={styles.senderName}>{msg.sender.name}</span>
                <CustomBadge
                  title={
                    (msg.sender.role || "").toLowerCase() === "both"
                      ? "Both (Final Reviewer & Designer)"
                      : msg.sender.role
                  }
                  bg={roleTheme.bg}
                  textColor={roleTheme.textColor}
                />
              </div>

              <span className={styles.timestamp}>{msg.timestamp}</span>
            </div>
          </div>
        </div>
        <div className={classNames(styles.messageContent, "")}>
          <div
            dangerouslySetInnerHTML={{ __html: msg.content }}
            className={styles.messageText}
          />
          {msg.attachments && msg.attachments.length > 0 && (
            <div
              className={classNames(
                styles.attachments,
                "flex items-center gap-3 mt-3",
              )}
            >
              {msg.attachments.map((attachment, idx) => (
                <a
                  key={idx}
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classNames(
                    styles.attachmentItem,
                    "cursor-pointer hover:opacity-80",
                  )}
                  onClick={(e) => {
                    if (!attachment.url) {
                      e.preventDefault();
                    }
                  }}
                >
                  {fileIconGetter(attachment.type)}
                  <span>{attachment.name}</span>
                </a>
              ))}
            </div>
          )}

          {hasReplies && (
            <div className="flex items-center gap-3 mt-3">
              <button
                className={classNames(
                  styles.replyButtonWithCount,
                  isReplyPanel && styles.replyButtonWithCountPanel,
                  "flex items-center gap-2",
                )}
                onClick={handleReplyClick}
              >
                <Icons.ChatOutline className={styles.replyIcon} />
                <span>
                  {(msg.repliesCount ?? 0) > 0 && (
                    <>
                      {msg.repliesCount}{" "}
                      {msg.repliesCount === 1 ? "reply" : "replies"}
                    </>
                  )}
                </span>
                {!isReplyPanel && (
                  <Icons.ChevRight className={styles.replyChevron} />
                )}
              </button>
              {lastReplyTime && (
                <span className={styles.lastReplyTimestamp}>
                  Last reply {formatDateTime24h(lastReplyTime)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {showReplyButton && (!hasReplies || isReplyPanel) && (
        <div
          className={classNames(
            "absolute sm:top-1/2 top-6 -translate-y-1/2 right-2",
            isReplyPanel && "sm:top-1/2 top-7 -translate-y-1/2 right-0",
          )}
        >
          <span className="md:block hidden">
            <button
              className={classNames(
                styles.replyButton,
                "flex items-center gap-2",
              )}
              onClick={handleReplyClick}
            >
              <Icons.ChatOutline className={styles.replyIcon} />
              Reply
              {isReplyPanel && (msg.repliesCount ?? 0) > 0 && (
                <span style={{ marginLeft: "4px", fontWeight: 500 }}>
                  {msg.repliesCount}
                </span>
              )}
            </button>
          </span>
          <span className="md:hidden block flex justify-end">
            <div ref={dropdownRef} className={styles.customDropdown}>
              <span
                ref={triggerRef}
                className={styles.dropdownTrigger}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDropdownOpen(!dropdownOpen);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Icons.VerticalDots />
              </span>
              {dropdownOpen && menuCoordinates && (
                <div
                  ref={menuRef}
                  className={styles.dropdownMenu}
                  style={{
                    position: "fixed",
                    // top: `${menuCoordinates.top}px`,
                    // right: `${menuCoordinates.right}px`,
                    minWidth: "90px",
                  }}
                >
                  <div
                    className={styles.dropdownOption}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleReplyClick();
                    }}
                  >
                    <Icons.ChatOutline />
                    Reply
                  </div>
                </div>
              )}
            </div>
          </span>
        </div>
      )}
    </div>
  );
};

export default MessageItem;
